import {
  DeploymentEventSummary,
  EventHistoryStatus,
  EventHistoryType,
} from "../common/ObjectTypes";

/**
 * How long another user's upload lock stays live after its last activity, in seconds
 * (AKL-897).
 *
 * Mirrors the API's steal window in `check_if_user_can_upload` (3600 s on the lock's
 * `last_update_ms`), which after AKL-895 is also the rule `end_upload` applies: while the
 * holder's lock is inside this window nobody else — admins included — may end the run, and
 * once it is past the window anyone may. AKL-896 owns changing either number; if the API's
 * window moves, this constant moves with it.
 *
 * The client cannot read `last_update_ms` — no endpoint returns it — so `canEndUpload`
 * measures from the latest event's `ts` instead, the second the In Progress row was written.
 * The AKL-893 heartbeat refreshes `last_update_ms` without touching that row, so `ts` is never
 * later than the lock's last activity: the age measured from it is an UPPER bound on the
 * lock's true age. Younger than the window from `ts` means certainly live; older means it
 * may be stale, and the server's answer (a 403 when it is not) is the authority.
 */
export const STALE_UPLOAD_LOCK_SECONDS = 3600;

/**
 * The two facts about the signed-in user the rule reads. Declared here rather than as
 * `Pick<Profile, "user_id" | "is_admin">`: `Profile` types `is_admin` as the literal `false`,
 * which would make an admin profile unrepresentable to this function and force every caller
 * and test that needs one through a cast. A `Profile` still satisfies this shape.
 */
export type EndUploadProfile = {
  user_id: string;
  is_admin?: boolean;
};

export type CanEndUploadArgs = {
  /** The deployment's latest event, from `GET /deployments/{id}/latest_event`. */
  latestEvent: DeploymentEventSummary | null;
  /** The signed-in user, or null while the profile loads. */
  profile: EndUploadProfile | null;
  /** True while this browser tab is itself uploading to this deployment. */
  isUploadingHere: boolean;
  /** The current time in milliseconds (`Date.now()`), passed in so the rule stays pure. */
  nowMs: number;
};

/**
 * Whether the Media Management page should offer the "End upload" action (AKL-897, AC1).
 *
 * Shown only when the latest event is an Upload still In Progress, and then to:
 * - the uploader (the event's `user_id` is the signed-in user's), at any age — except while
 *   this tab is the one uploading, where the upload panel's own Cancel button ends that run
 *   and a second control racing it would help nobody;
 * - an admin, only when the lock may be stale: `STALE_UPLOAD_LOCK_SECONDS` or more have
 *   passed since the event's `ts`. An event with no usable `ts` is treated as fresh, the
 *   conservative answer.
 *
 * User ids are compared case-insensitively: both are UUID strings, one from the profile
 * endpoint and one stored by the API from `str(user.user_id)`.
 * @param args the latest event, the profile, whether this tab is uploading, and the clock
 * @returns true when the action should be offered
 */
export function canEndUpload({
  latestEvent,
  profile,
  isUploadingHere,
  nowMs,
}: CanEndUploadArgs): boolean {
  if (!latestEvent || !profile) return false;
  if (
    latestEvent.type !== EventHistoryType.Upload ||
    latestEvent.status !== EventHistoryStatus.InProgress
  ) {
    return false;
  }
  if (isUploadingHere) return false;
  if (
    typeof latestEvent.user_id === "string" &&
    typeof profile.user_id === "string" &&
    latestEvent.user_id.toLowerCase() === profile.user_id.toLowerCase()
  ) {
    return true;
  }
  if (!profile.is_admin) return false;
  if (typeof latestEvent.ts !== "number" || !Number.isFinite(latestEvent.ts)) {
    return false;
  }
  return nowMs / 1000 - latestEvent.ts >= STALE_UPLOAD_LOCK_SECONDS;
}
